import { ReactNode } from 'react';

import { AbilityContext } from '@app/ability/Can';
import { useSelector } from '@app/hooks';
import createAbilityFor from '@app/ability/ability';

import { selectUserRepresentative, usePermissions } from '../../user';

interface AbilityProviderProps {
  children: ReactNode;
  role: 'employee' | 'company' | 'general';
}

export const AbilityProvider = ({ children, role }: AbilityProviderProps) => {
  const userRepresentative = useSelector(selectUserRepresentative);

  const {
    view_events: canSeeEvents,
    view_event_to_paper: canSeeEventsToPaper,
    view_employees: canSeeEmployees,
    view_policies: canSeePolicies,
    view_company: canSeeCompany,
    view_company_profiles: canSeeCompanyProfiles,
    view_settings: canSeeSettings,
    view_attorneys: canSeeAttorneys,
    view_candidates: canSeeCandidates,
    view_vacations: canSeeVacations,
    view_vacations_settings: canSeeVacationsSettings,
    view_payslips: canSeePayslips,
    view_smev_errors: canSeeSmevErrors,
    view_goal_management: canSeeGoalManagement,
    view_personal_profile: canSeePersonalProfile,
    view_absences: canSeeAbsences,
    view_competencies: canSeeCompetencies,
    view_units: canSeeUnits,
    view_social_news: canSeeNews,
    view_social_target_audience: canSeeTargetAudience,
    view_social_categories: canSeeSocialCategories,
    view_social_access: canSeeSocialAccess,
    view_social_surveys: canSeeSocialSurveys,
    view_social_communities: canSeeSocialCommunities,
    view_social_feed: canSeeSocialFeed,
    view_account_settings: canSeeAccountSettings,
    view_account_dashboard_settings: canSeeAccountDashboardSettings,
    view_account_access_settings: canSeeAccountAccessSettings,
    view_account_general_settings: canSeeAccountGeneralSettings,
    view_social_widget_content: canSeeSocialWidgetContent,
    view_business_trips: canSeeBusinessTrips,
    view_process_catalog: canSeeProcessCatalog,
    view_social_favorites: canSeeFavorites,
    view_quick_search: canSeeQuickSearch,
  } = usePermissions(role);

  const ability = createAbilityFor({
    role,
    userRepresentative,
    canSeeEvents,
    canSeeEventsToPaper,
    canSeeEmployees,
    canSeePolicies,
    canSeeCompany,
    canSeeCompanyProfiles,
    canSeeSettings,
    canSeeCandidates,
    canSeeAttorneys,
    canSeeVacations,
    canSeeVacationsSettings,
    canSeePayslips,
    canSeeGoalManagement,
    canSeeSmevErrors,
    canSeePersonalProfile,
    canSeeAbsences,
    canSeeCompetencies,
    canSeeUnits,
    canSeeNews,
    canSeeSocialCategories,
    canSeeSocialAccess,
    canSeeSocialSurveys,
    canSeeSocialCommunities,
    canSeeSocialFeed,
    canSeeAccountSettings,
    canSeeAccountDashboardSettings,
    canSeeAccountAccessSettings,
    canSeeAccountGeneralSettings,
    canSeeSocialWidgetContent,
    canSeeBusinessTrips,
    canSeeProcessCatalog,
    canSeeTargetAudience,
    canSeeFavorites,
    canSeeQuickSearch,
  });

  return (
    <AbilityContext.Provider value={ability}>
      {children}
    </AbilityContext.Provider>
  );
};
